import { Box, Stack, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";

/**
 * The MultiDoc wordmark.
 *
 * `onBrand`: drawn on the brand-coloured suite header or sign-in panel (MultiDoc
 * design system). There the mark is a single white silhouette, as ecm-frontend
 * treats its default logo, because the purple half would clash with the blue.
 *
 * Two files, not one with a filter: the mark is purple "Multi" plus near-black
 * "Doc", and on the navy nav the black half disappears entirely. The reversed
 * variant repaints that half white and lifts the purple along its own hue so
 * both halves hold against a dark ground. Anything drawn on the nav must ask
 * for `reversed`.
 */
export function Wordmark({ reversed = false, onBrand = false, height = 26, sx }: {
  reversed?: boolean;
  onBrand?: boolean;
  height?: number;
  sx?: SxProps<Theme>;
}) {
  return (
    <Box
      component="img"
      src={reversed ? "/brand/multidoc-wordmark-reversed.png" : "/brand/multidoc-wordmark.webp"}
      alt="MultiDoc"
      // `alignSelf` matters: in a flex column the default `stretch` beats
      // `width: auto` and squashes the wordmark to the container's width.
      sx={{
        height, width: "auto", alignSelf: "flex-start", flexShrink: 0, display: "block",
        ...(onBrand && { filter: "brightness(0) invert(1)" }),
        ...sx,
      }}
    />
  );
}

export function Mark({ reversed = false, size = 32, sx }: {
  reversed?: boolean;
  size?: number;
  sx?: SxProps<Theme>;
}) {
  return (
    <Box
      component="img"
      src={reversed ? "/brand/multidoc-mark-reversed.png" : "/brand/multidoc-mark.webp"}
      alt="MultiDoc"
      sx={{ height: size, width: size, display: "block", ...sx }}
    />
  );
}

/**
 * Wordmark plus the product name it belongs to. "MultiDoc" is the company mark
 * and "Console" is this product, so they are set differently — the same way
 * "MultiDoc ECM" is set in the product itself.
 */
export function BrandLockup({ reversed = false, onBrand = false, height = 24 }: {
  reversed?: boolean;
  /** On the brand-coloured header: an all-white lockup. */
  onBrand?: boolean;
  height?: number;
}) {
  return (
    <Stack direction="row" alignItems="baseline" spacing={1.1}>
      <Wordmark reversed={reversed} onBrand={onBrand} height={height} sx={{ alignSelf: "center" }} />
      <Typography
        component="span"
        sx={{
          fontSize: height * 0.62,
          fontWeight: 400,
          color: onBrand ? "inherit" : reversed ? "common.white" : "text.secondary",
          lineHeight: 1,
          alignSelf: "center",
        }}
      >
        Console
      </Typography>
    </Stack>
  );
}
